<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import DayChart from '@/components/dashboards/DayChart.vue'

const props = defineProps({
  report: { type: Object, required: true },
})

const { t } = useI18n()

// Validated for colour-blind readers and for contrast on the light surface
const COLOR = { blue: '#0588f0', teal: '#008573', amber: '#ab6400' }
const WDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const totals = computed(() => props.report.totals)
const days = computed(() => props.report.days)
const countPoints = computed(() =>
  days.value.map(d => ({ day: d.day, value: d.conversations }))
)
const replyPoints = computed(() =>
  days.value.map(d => ({ day: d.day, value: d.first_reply_median_s }))
)
const hourPoints = computed(() =>
  props.report.by_hour.map((value, hour) => ({
    day: String(hour),
    label: String(hour).padStart(2, '0'),
    value,
  }))
)
const wdayPoints = computed(() =>
  props.report.by_wday.map((value, i) => ({
    day: WDAYS[i],
    label: t(`DASHBOARDS.LOAD.WDAYS.${WDAYS[i]}`),
    value,
  }))
)
const peakHour = computed(() => {
  const top = Math.max(...props.report.by_hour)
  return top ? props.report.by_hour.indexOf(top) : null
})
const fmt = n => (n == null ? '—' : new Intl.NumberFormat('ru-RU').format(n))
const fmtMin = s => {
  if (s == null) return '—'
  return s < 60
    ? `${s} ${t('MONITORING.SECONDS')}`
    : `${Math.round(s / 60)} ${t('MONITORING.MINUTES')}`
}
const fmtPct = v => (v == null ? '—' : `${v} %`)
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.CONVERSATIONS')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.conversations)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.PER_DAY', { count: fmt(totals.per_day) })
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.RESOLVED')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.resolved)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.RESOLVED_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.REPLY')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmtMin(totals.first_reply_median_s)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.REPLY_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.P90')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmtMin(totals.first_reply_p90_s)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.P90_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.QUICK')
        }}</span>
        <span class="text-2xl font-semibold text-n-teal-11">{{
          fmtPct(totals.first_reply_5min_share)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.QUICK_SUB')
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.OVERNIGHT')
        }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          fmt(totals.waited_overnight)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('DASHBOARDS.LOAD.OVERNIGHT_SUB')
        }}</span>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.LOAD.COUNT_BY_DAY') }}
        </h2>
        <DayChart :points="countPoints" kind="bars" :color="COLOR.blue" />
      </section>
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.LOAD.REPLY_BY_DAY') }}
        </h2>
        <DayChart :points="replyPoints" :color="COLOR.amber" :format="fmtMin" />
      </section>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.LOAD.BY_HOUR') }}
        </h2>
        <DayChart :points="hourPoints" kind="bars" :color="COLOR.teal" />
        <span v-if="peakHour != null" class="text-xs text-n-slate-11">
          {{ $t('DASHBOARDS.LOAD.PEAK', { hour: peakHour }) }}
        </span>
      </section>
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.LOAD.BY_WDAY') }}
        </h2>
        <DayChart :points="wdayPoints" kind="bars" :color="COLOR.teal" />
        <span class="text-xs text-n-slate-11">
          {{
            $t('DASHBOARDS.LOAD.FAILED', { count: fmt(totals.failed_messages) })
          }}
        </span>
      </section>
    </div>
  </div>
</template>
